import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';

// Feature-explainer panel shown at the top of each module — DEMO ENVIRONMENT
// ONLY. Affiliates giving demos aren't platform experts, so each page opens
// with a short product explanation. The copy is written for the prospect's
// eyes too: it explains the feature, never references the demo itself.
//
// Keyed by route prefix (longest match wins). Multi-feature pages get
// feature bullets rather than separate blocks.
const GUIDES = [
  {
    path: '/organizations',
    title: 'Multi-Venue Management',
    body: 'Operators with more than one property manage every venue from a single account.',
    points: [
      'Each organization has its own booths, branding, staff, and revenue reporting.',
      'Owners switch venues from the dropdown in the sidebar — all data scopes instantly.',
    ],
  },
  {
    path: '/users-access',
    title: 'Team & Access Control',
    body: 'Give staff exactly the access they need — nothing more.',
    points: [
      'Roles: owners see everything for their venue; managers can be limited to specific booths; salespeople get commission tracking.',
      'Invites are sent by email with secure account setup — no shared passwords.',
    ],
  },
  {
    path: '/users',
    title: 'Guest Accounts',
    body: 'Guests who create an account keep their photos across every visit — and you can see who keeps coming back.',
    points: [
      'Visit counts and last-seen dates per venue and per booth.',
      'Repeat guests are your promoters: filter by booth to see who uses which activation.',
    ],
  },
  {
    path: '/customers',
    title: 'Customer Contact List',
    body: 'Every opt-in contact the booths collect, in one exportable list.',
    points: [
      'Combines account signups, checkout emails, and free-session contact capture.',
      'Export to CSV for your email or SMS marketing tools.',
    ],
  },
  {
    path: '/sessions',
    title: 'Session History',
    body: 'Every booth session, live — photos, prints, and sharing in one record.',
    points: [
      'Open a session to see its photos, photo strip, GIF, and the QR code guests scan to download.',
      'Payment linkage shows exactly what was charged for each session.',
      'Guests control their privacy: opt-outs and deletion requests are honored automatically.',
    ],
  },
  {
    path: '/payments',
    title: 'Payments & Ledger',
    body: 'Card payments flow straight through Stripe with a full audit trail.',
    points: [
      'Every charge links back to its booth and session.',
      'Refunds and disputes are tracked with status — revenue math always reflects reality.',
    ],
  },
  {
    path: '/booths',
    title: 'Live Fleet Monitoring',
    body: 'Every booth reports in from the field — no site visits needed to know things are running.',
    points: [
      'Online status via heartbeat; camera, printer, and network health at a glance.',
      'Printer media levels tracked per booth so paper never runs out mid-event.',
      'Per-booth configuration: session pricing, free/paid modes, enabled filters, and print themes.',
    ],
  },
  {
    path: '/themes',
    title: 'Print Branding',
    body: 'Every print leaves the booth carrying the venue\'s brand.',
    points: [
      'Full design control: colors, frame patterns, background art, and logo placement.',
      'Four digital formats — classic strips, postcards, GIFs, and polaroids — each individually themeable.',
    ],
  },
  {
    path: '/screen-themes',
    title: 'Screen Experiences',
    body: 'The booth\'s screens are venue-branded from the moment guests walk up.',
    points: [
      'Idle screens run photo or video slideshows to attract guests between sessions.',
      'Welcome screens carry custom copy, colors, and branding per venue.',
    ],
  },
  {
    path: '/packages',
    title: 'Pricing Packages',
    body: 'Credit packages are fully customizable — price points, bundle sizes, badges, and promotions.',
    points: [
      'Guests buy credits at the booth or online; venues set the menu.',
      'Highlight a package with badges like "Most Popular" to steer purchases.',
    ],
  },
  {
    path: '/filters',
    title: 'Photo Filters & AR',
    body: 'From classic black & white to AR face effects and virtual backgrounds.',
    points: [
      'Color presets, branded overlay frames, AR effects, and greenscreen-style backgrounds.',
      'Each booth enables its own filter lineup to match the event.',
    ],
  },
  {
    path: '/social-media',
    title: 'Social Media Studio',
    body: 'The booth becomes a content engine: AI turns the best sessions into ready-to-post social content.',
    points: [
      'Every session is scored by AI — the inbox surfaces the most postable moments first.',
      'Captions and hashtags are drafted automatically; approve, edit, or reject in one click.',
      'Approved content can be scheduled to a posting calendar.',
    ],
  },
  {
    path: '/reports',
    title: 'Financial Reports',
    body: 'Monthly statements generated automatically from real payment data.',
    points: [
      'Revenue, session counts, and payout splits per booth and per venue.',
      'Statements are immutable snapshots — what you report is what happened.',
    ],
  },
  {
    path: '/payout-breakdown',
    title: 'Revenue Sharing',
    body: 'Transparent splits: every dollar is divided by the rates configured per booth.',
    points: [
      'Venues see their share in real time — no waiting for month-end.',
    ],
  },
  {
    path: '/discount-codes',
    title: 'Promo & Comp Codes',
    body: 'Generate single-use or multi-use codes for promotions, VIPs, and make-goods.',
  },
  {
    path: '/',
    title: 'Venue Dashboard',
    body: 'The owner\'s daily view: how every booth is earning, at a glance.',
    points: [
      'Revenue, sessions, photos, and guest KPIs with trends against the prior period.',
      'Booth leaderboard shows which placements earn the most.',
      'All-time charts stay exact — powered by nightly rollups, not estimates.',
    ],
  },
];

const COLLAPSE_KEY = 'demo-feature-guide-collapsed';

const DemoFeatureGuide = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');

  if (import.meta.env.VITE_FIREBASE_PROJECT_ID !== 'socialsnap-demo') return null;

  const guide = GUIDES.find((g) => (g.path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(g.path)));
  if (!guide) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
  };

  return (
    <div className="mb-5 bg-gradient-to-br from-primary-50 to-white border border-primary-100 rounded-xl overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-800">
          <Info className="w-4 h-4 text-primary-600" /> {guide.title}
        </span>
        {collapsed
          ? <ChevronDown className="w-4 h-4 text-primary-400" />
          : <ChevronUp className="w-4 h-4 text-primary-400" />}
      </button>
      {!collapsed && (
        <div className="px-4 pb-3.5 -mt-0.5">
          <p className="text-sm text-gray-700">{guide.body}</p>
          {guide.points && (
            <ul className="mt-2 space-y-1">
              {guide.points.map((p) => (
                <li key={p} className="text-sm text-gray-600 flex gap-2">
                  <span className="text-primary-400 flex-shrink-0">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default DemoFeatureGuide;
